import { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Bell, FileText, Inbox, Lock, Package, ShoppingCart, X } from 'lucide-react';
import { BrandMark } from '../ui';
import { useFocusTrap } from '../../utils/accessibility';

/**
 * The 236px ink column.
 *
 * It lists the routes that exist. `/admin/users`, `/admin/transactions`
 * and `/admin/settings` used to sit here pointing at nothing — a nav item that
 * 404s is worse than a missing one, because the operator plans work around it.
 *
 * No unread counters: those numbers would have to come from the queue endpoint,
 * and a badge that is always "7" is a fake number in a place operators trust.
 */

const ADMIN_ITEMS = [
  { label: 'Antrian', to: '/admin', end: true, icon: Bell },
  { label: 'Pesanan', to: '/admin/pesanan', end: false, icon: ShoppingCart },
  { label: 'Penawaran', to: '/admin/penawaran', end: false, icon: FileText },
  { label: 'Toko', to: '/admin/toko', end: false, icon: Package },
  { label: 'Lisensi', to: '/admin/lisensi', end: false, icon: Lock },
  { label: 'Titip lisensi', to: '/admin/titip-lisensi', end: false, icon: Inbox },
];

const itemClass = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-[44px] items-center gap-[11px] rounded-thumb px-[14px] py-3 text-[15px] no-underline transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
    isActive ? 'bg-accent font-extrabold text-ink' : 'font-bold text-muted-dark hover:text-white'
  }`;

function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <>
      <Link
        to="/"
        onClick={onNavigate}
        className="mb-[30px] inline-block px-[6px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        aria-label="Jualan, panel admin. Ke situs publik"
      >
        <BrandMark variant="admin" size="sm" />
      </Link>
      <nav aria-label="Navigasi admin" className="flex flex-col gap-1">
        {ADMIN_ITEMS.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} className={itemClass}>
            <item.icon size={18} aria-hidden="true" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </>
  );
}

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AdminSidebar = ({ isOpen, onClose }: AdminSidebarProps) => {
  const drawerRef = useFocusTrap(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      <aside className="hidden bg-ink px-[18px] py-6 lg:block">
        <SidebarBody />
      </aside>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-ink/60 lg:hidden" onClick={onClose} aria-hidden="true" />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigasi admin"
            className="fixed left-0 top-0 z-50 h-full w-[236px] overflow-y-auto bg-ink px-[18px] py-6 lg:hidden"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Tutup menu"
              className="mb-4 inline-flex h-[44px] w-[44px] items-center justify-center rounded-pill text-muted-dark transition-colors duration-150 ease-out hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X size={22} aria-hidden="true" />
            </button>
            <SidebarBody onNavigate={onClose} />
          </div>
        </>
      )}
    </>
  );
};

export default AdminSidebar;
